class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.DEBUG = true; // Debug-Modus für Diagnose
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    if (this.DEBUG) console.log('[PredictiveSearch] Init:', { input: this.input, results: this.predictiveSearchResults });
    
    if (!this.input) {
      console.error('[PredictiveSearch] Input nicht gefunden!');
      return;
    }
    if (!this.predictiveSearchResults) {
      console.error('[PredictiveSearch] Results Container nicht gefunden!');
      return;
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (!form) {
      console.error('[PredictiveSearch] Form mit class "search" nicht gefunden!');
      return;
    }
    if (this.DEBUG) console.log('[PredictiveSearch] Form gefunden:', form);
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    
    // Klick außerhalb schließt das Dropdown (mit Verzögerung für bessere UX)
    document.addEventListener('click', (event) => {
      // Kurze Verzögerung um sicherzustellen, dass andere Events zuerst verarbeitet werden
      setTimeout(() => {
        if (this.isOpen && !this.contains(event.target)) {
          if (this.DEBUG) console.log('[PredictiveSearch] Click outside - closing');
          this.close();
        }
      }, 100);
    });
    
    // Escape-Taste schließt das Dropdown
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        if (this.DEBUG) console.log('[PredictiveSearch] Escape pressed - closing');
        this.close();
        this.input.blur();
      }
    });
    
    // Mausinteraktion auf dem Dropdown verhindert Schließen
    this.predictiveSearchResults.addEventListener('mousedown', (event) => {
      // Verhindere, dass focusout ausgelöst wird
      event.preventDefault();
      if (this.DEBUG) console.log('[PredictiveSearch] Mousedown on results - preventing focus loss');
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    if (this.DEBUG) console.log('[PredictiveSearch] onChange:', searchTerm);

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();
    if (this.DEBUG) console.log('[PredictiveSearch] onFocus:', searchTerm);

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut(event) {
    // NICHT automatisch schließen bei focusout
    // Das Dropdown wird nur durch expliziten Klick außerhalb oder Escape geschlossen
    if (this.DEBUG) console.log('[PredictiveSearch] onFocusOut - ignored (using click-outside instead)');
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    if (this.DEBUG) console.log('[PredictiveSearch] getSearchResults:', searchTerm);
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      if (this.DEBUG) console.log('[PredictiveSearch] Using cached results');
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const url = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=4&section_id=predictive-search`;
    if (this.DEBUG) console.log('[PredictiveSearch] Fetching:', url);
    
    fetch(url)
      .then((response) => {
        if (this.DEBUG) console.log('[PredictiveSearch] Response status:', response.status);
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (this.DEBUG) console.log('[PredictiveSearch] Response text length:', text.length);
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const section = parsed.querySelector('#shopify-section-predictive-search');
        if (!section) {
          console.error('[PredictiveSearch] Section nicht gefunden in Response!', text.substring(0, 500));
          return;
        }
        const resultsMarkup = section.innerHTML;
        if (this.DEBUG) console.log('[PredictiveSearch] Results markup length:', resultsMarkup.length);
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        console.error('[PredictiveSearch] Fetch error:', error);
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (this.DEBUG) console.log('[PredictiveSearch] renderSearchResults');
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) {
      this.setLiveRegionText(countEl.textContent);
    }
  }

  getResultsMaxHeight() {
    const header = document.getElementById('shopify-section-header') || document.getElementById('shopify-section-header-mega');
    if (header) {
      this.resultsMaxHeight = window.innerHeight - header.getBoundingClientRect().bottom;
    } else {
      this.resultsMaxHeight = 400;
    }
    return this.resultsMaxHeight;
  }

  open() {
    if (this.DEBUG) console.log('[PredictiveSearch] OPEN');
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (this.DEBUG) console.log('[PredictiveSearch] CLOSE');
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
